import { getRelativePosition } from "chart.js/helpers";

const DRAG_THRESHOLD = 6;

export const chartDragSelectBridge = {
  handlers: null,
};

const isPointerInsideChartArea = (chart, x, y) => {
  const { chartArea } = chart;
  if (!chartArea) return false;
  return (
    x >= chartArea.left &&
    x <= chartArea.right &&
    y >= chartArea.top &&
    y <= chartArea.bottom
  );
};

export const chartDragSelectPlugin = {
  id: "chartDragSelect",
  afterInit(chart) {
    const canvas = chart.canvas;
    const doc = canvas.ownerDocument;
    let pointerDownOnChart = false;

    const getXY = (event) => getRelativePosition(event, chart);

    const onMouseDown = (event) => {
      const handlers = chartDragSelectBridge.handlers;
      if (!handlers) return;
      if (event.button !== 0) return;
      if (event.ctrlKey || event.metaKey) return;

      const { x, y } = getXY(event);
      if (!isPointerInsideChartArea(chart, x, y)) return;

      pointerDownOnChart = true;
      handlers.onDragStart(chart, x, y);
      event.preventDefault();
      event.stopPropagation();
    };

    const onMouseMove = (event) => {
      const handlers = chartDragSelectBridge.handlers;
      if (!handlers) return;

      const isDragging = handlers.isDraggingChart?.(chart);
      if (!pointerDownOnChart && !isDragging) return;

      const { x, y } = getXY(event);
      handlers.onDragMove(chart, x, y);
    };

    const onMouseUp = (event) => {
      const handlers = chartDragSelectBridge.handlers;
      if (!handlers) return;

      const isDragging = handlers.isDraggingChart?.(chart);
      if (!pointerDownOnChart && !isDragging) return;

      const { x, y } = getXY(event);
      handlers.onDragEnd(chart, x, y);
      pointerDownOnChart = false;
    };

    canvas.addEventListener("mousedown", onMouseDown);
    doc.addEventListener("mousemove", onMouseMove);
    doc.addEventListener("mouseup", onMouseUp);

    const onTouchStart = (event) => {
      const handlers = chartDragSelectBridge.handlers;
      if (!handlers || event.touches.length !== 1) return;

      const touch = event.touches[0];
      const { x, y } = getRelativePosition(touch, chart);
      if (!isPointerInsideChartArea(chart, x, y)) return;

      pointerDownOnChart = true;
      handlers.onDragStart(chart, x, y);
      event.preventDefault();
    };

    const onTouchMove = (event) => {
      const handlers = chartDragSelectBridge.handlers;
      if (!handlers || event.touches.length !== 1) return;

      const isDragging = handlers.isDraggingChart?.(chart);
      if (!pointerDownOnChart && !isDragging) return;

      const touch = event.touches[0];
      const { x, y } = getRelativePosition(touch, chart);
      handlers.onDragMove(chart, x, y);
      if (isDragging) event.preventDefault();
    };

    const onTouchEnd = (event) => {
      const handlers = chartDragSelectBridge.handlers;
      if (!handlers) return;

      const isDragging = handlers.isDraggingChart?.(chart);
      if (!pointerDownOnChart && !isDragging) return;

      const touch = event.changedTouches[0];
      const { x, y } = getRelativePosition(touch, chart);
      handlers.onDragEnd(chart, x, y);
      pointerDownOnChart = false;
    };

    canvas.addEventListener("touchstart", onTouchStart, { passive: false });
    doc.addEventListener("touchmove", onTouchMove, { passive: false });
    doc.addEventListener("touchend", onTouchEnd);

    chart._dragSelectCleanup = () => {
      canvas.removeEventListener("mousedown", onMouseDown);
      doc.removeEventListener("mousemove", onMouseMove);
      doc.removeEventListener("mouseup", onMouseUp);
      canvas.removeEventListener("touchstart", onTouchStart);
      doc.removeEventListener("touchmove", onTouchMove);
      doc.removeEventListener("touchend", onTouchEnd);
    };
  },
  beforeDestroy(chart) {
    chart._dragSelectCleanup?.();
    delete chart._dragSelectCleanup;
  },
  afterDraw(chart) {
    const handlers = chartDragSelectBridge.handlers;
    if (!handlers) return;

    const rect = handlers.getDragRect(chart);
    if (!rect) return;

    const { ctx, chartArea } = chart;
    if (!chartArea) return;

    const left = Math.max(chartArea.left, rect.left);
    const right = Math.min(chartArea.right, rect.right);
    if (right <= left) return;

    ctx.save();
    ctx.fillStyle = "rgba(143, 173, 21, 0.25)";
    ctx.strokeStyle = "rgba(28, 66, 32, 0.65)";
    ctx.lineWidth = 1.5;
    ctx.fillRect(left, chartArea.top, right - left, chartArea.bottom - chartArea.top);
    ctx.strokeRect(left, chartArea.top, right - left, chartArea.bottom - chartArea.top);
    ctx.restore();
  },
};

export const getChartIndicesInPixelRange = (chart, minX, maxX) => {
  const primaryLabels = ["UMIDADE", "TEMPERATURA", "DENSIDADE", "PH"];
  let meta = null;

  for (let i = 0; i < chart.data.datasets.length; i += 1) {
    if (primaryLabels.includes(chart.data.datasets[i].label)) {
      meta = chart.getDatasetMeta(i);
      break;
    }
  }

  if (!meta?.data?.length) return [];

  const indices = [];
  meta.data.forEach((point, index) => {
    if (point && !point.skip && point.x >= minX && point.x <= maxX) {
      indices.push(index);
    }
  });

  return indices;
};

export const DRAG_SELECT_THRESHOLD = DRAG_THRESHOLD;
